import React, { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, CirclePlus, Pencil, Star, Check, X } from "lucide-react";
import type { CheckoutSettingsResponse } from "@/types/booking-checkout-settings";

const Label = ({ children, className }: any) => (
  <label className={`text-2xl font-medium ${className || ""}`}>
    {children}
  </label>
);

interface ReviewsTabProps {
  settings: CheckoutSettingsResponse | null;
  setSettings: React.Dispatch<
    React.SetStateAction<CheckoutSettingsResponse | null>
  >;
}

export const ReviewsTab = ({ settings, setSettings }: ReviewsTabProps) => {
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const isIndividualCategories =
    settings?.isReviewCategories ??
    (settings?.reviewCategories?.length ?? 0) > 0;

  const updateSettings = (patch: Partial<CheckoutSettingsResponse>) => {
    setSettings((currentSettings) =>
      currentSettings ? { ...currentSettings, ...patch } : currentSettings,
    );
  };

  const handleToggleIndividualCategories = (checked: boolean) => {
    if (!checked) {
      setIsAddingCategory(false);
      setEditingIndex(null);
      setEditingName("");
    }

    updateSettings({ isReviewCategories: checked });
  };

  const handleAddCategory = () => {
    if (newCategoryName.trim()) {
      const newCategories = [
        ...(settings?.reviewCategories || []),
        newCategoryName.trim(),
      ];
      updateSettings({ reviewCategories: newCategories });
      setNewCategoryName("");
      setIsAddingCategory(false);
    }
  };

  const handleDeleteCategory = (index: number) => {
    const newCategories = [...(settings?.reviewCategories || [])];
    newCategories.splice(index, 1);
    updateSettings({ reviewCategories: newCategories });
  };

  const startEditing = (index: number, name: string) => {
    setEditingIndex(index);
    setEditingName(name);
  };

  const saveEditing = () => {
    if (editingName.trim() && editingIndex !== null) {
      const newCategories = [...(settings?.reviewCategories || [])];
      newCategories[editingIndex] = editingName.trim();
      updateSettings({ reviewCategories: newCategories });
      setEditingIndex(null);
      setEditingName("");
    }
  };

  const cancelEditing = () => {
    setEditingIndex(null);
    setEditingName("");
  };

  return (
    <div className="space-y-6">
      {/* Leave a review Toggle */}
      <div className="flex items-center gap-2">
        <Switch
          checked={settings?.isLeaveReview ?? false}
          onCheckedChange={(checked) =>
            updateSettings({ isLeaveReview: checked })
          }
        />
        <Label>Leave a review</Label>
      </div>

      {settings?.isLeaveReview && (
        <div className="ml-6 space-y-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={settings?.isSkipReview ?? false}
              onCheckedChange={(checked) =>
                updateSettings({ isSkipReview: checked })
              }
            />
            <Label className="text-xl">Skip button</Label>
          </div>

          {/* Individual Categories Toggle */}
          <div className="flex items-center gap-2">
            <Switch
              checked={isIndividualCategories}
              onCheckedChange={handleToggleIndividualCategories}
            />
            <Label className="text-xl">Individual categories</Label>
          </div>

          {isIndividualCategories && (
            <div className="ml-6 space-y-2">
              {/* Categories List */}
              {settings?.reviewCategories?.map(
                (category: string, index: number) => (
                  <div
                    key={index}
                    className="flex items-center justify-between border-b border-gray-200 py-2"
                  >
                    {editingIndex === index ? (
                      <div className="flex flex-1 items-center gap-2">
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="h-8"
                          autoFocus
                        />
                        <Button
                          type="button"
                          variant="new_ghost"
                          size="icon_sm"
                          onClick={saveEditing}
                          aria-label="Save category"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="new_ghost"
                          size="icon_sm"
                          onClick={cancelEditing}
                          aria-label="Cancel editing"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-1 items-center gap-4">
                          <span className="min-w-[100px] text-sm font-medium">
                            {category}
                          </span>
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className="h-4 w-4 fill-gray-300 text-gray-300"
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="new_ghost"
                            size="icon_sm"
                            onClick={() => startEditing(index, category)}
                            aria-label={`Edit ${category}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="new_ghost"
                            size="icon_sm"
                            onClick={() => handleDeleteCategory(index)}
                            aria-label={`Delete ${category}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ),
              )}

              {/* Add New Category */}
              <div className="flex items-center gap-3 border-b border-gray-200 py-2">
                <div className="flex flex-1 items-center gap-2">
                  <div className="flex size-8 items-center justify-center">
                    <Button
                      type="button"
                      variant="new_ghost"
                      size="icon_sm"
                      onClick={() => setIsAddingCategory(true)}
                      aria-label="Add category"
                    >
                      <CirclePlus />
                    </Button>
                  </div>
                  {isAddingCategory && (
                    <div className="flex flex-1 items-center gap-2">
                      <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Category name"
                        className="h-8"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddCategory();
                          if (e.key === "Escape") setIsAddingCategory(false);
                        }}
                      />
                      <Button
                        type="button"
                        variant="new_ghost"
                        size="icon_sm"
                        onClick={handleAddCategory}
                        aria-label="Save category"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="new_ghost"
                        size="icon_sm"
                        onClick={() => setIsAddingCategory(false)}
                        aria-label="Cancel category"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Comments Toggle */}
      <div className="flex items-center gap-2">
        <Switch
          checked={settings?.isLeaveComment ?? false}
          onCheckedChange={(checked) =>
            updateSettings({ isLeaveComment: checked })
          }
        />
        <Label>Comments</Label>
      </div>
    </div>
  );
};
